import { useAreaScenario } from "../../lib/useScenario";
import { euroShort, pct, pricePerM2 } from "../../lib/format";
import {
  useActiveScenario,
  useScenarioStore,
  type Scenario,
  type ScenarioArea,
} from "../../state/useScenarioStore";
import { levelDef } from "../../map/levels";
import { useMap } from "../../map/useMapStore";

/**
 * The areas in the active scenario, priced at the same flat size.
 *
 * One number per area is easy to get anywhere; the same hypothetical flat
 * priced across five neighbourhoods at once is the thing that settles an
 * argument.
 */
export function PinnedAreas() {
  const scenario = useActiveScenario();

  if (scenario.areas.length === 0) return null;

  return (
    <section className="mt-4 border-t border-line pt-3">
      <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
        Areas
      </h3>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-ink-soft">
            <th className="pb-1 text-left font-medium">Area</th>
            <th className="pb-1 text-right font-medium">Today</th>
            <th className="pb-1 text-right font-medium">+{scenario.horizon} yr</th>
            <th className="w-4" />
          </tr>
        </thead>
        <tbody>
          {scenario.areas.map((area) => (
            <AreaRow
              key={`${area.scope}:${area.code}`}
              area={area}
              scenario={scenario}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AreaRow({ area, scenario }: { area: ScenarioArea; scenario: Scenario }) {
  const removeArea = useScenarioStore((s) => s.removeArea);
  const map = useMap();
  const { projection, rate, isLoading } = useAreaScenario(area, scenario);

  const gain =
    projection && projection.base.value > 0
      ? (projection.final - projection.base.value) / projection.base.value
      : null;

  return (
    <tr className="border-t border-line align-top">
      <td className="py-1 pr-1">
        <button
          onClick={() => map?.flyTo({ center: [area.lon, area.lat] })}
          className="block max-w-[7.5rem] truncate text-left font-medium hover:text-accent"
          title={`${area.name} · ${levelDef(area.scope).noun}`}
        >
          {area.name}
        </button>
        <span className="block text-[10px] tabular-nums text-ink-soft">
          {projection ? pricePerM2(projection.base.perM2) : "—"}
        </span>
      </td>
      <td className="py-1 text-right tabular-nums">
        {isLoading ? "…" : projection ? euroShort(projection.base.value) : "—"}
      </td>
      <td className="py-1 text-right tabular-nums">
        {isLoading ? "…" : projection ? euroShort(projection.final) : "—"}
        <span
          className={`block text-[10px] ${
            gain === null
              ? "text-ink-soft"
              : gain >= 0
                ? "text-[#2e7d32]"
                : "text-[#cf3a2e]"
          }`}
        >
          {gain === null ? "" : `${pct(gain)} · ${pct(rate)}/yr`}
        </span>
      </td>
      <td className="py-1 text-right">
        <button
          onClick={() => removeArea(area.scope, area.code)}
          className="rounded px-0.5 text-ink-soft hover:bg-surface-alt"
          aria-label={`Remove ${area.name} from ${scenario.name}`}
        >
          ✕
        </button>
      </td>
    </tr>
  );
}
